import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { useCallback, useEffect, useState } from "react";

import { TestRow } from "../TestRow";
import type { TestResult } from "../types";
import { StubTabBody, StubTabRow } from "./_StubTab";

const blank: TestResult = { status: "idle" };

export function WalletTab(): JSX.Element {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceTick, setBalanceTick] = useState(0);
  const [airdropAmount, setAirdropAmount] = useState<string>("1");
  const [airdropResult, setAirdropResult] = useState<TestResult>(blank);

  useEffect(() => {
    let cancelled = false;
    if (!wallet.publicKey) {
      setBalance(null);
      return;
    }
    void connection
      .getBalance(wallet.publicKey)
      .then((lamports) => {
        if (!cancelled) setBalance(lamports);
      })
      .catch(() => {
        if (!cancelled) setBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [connection, wallet.publicKey, balanceTick]);

  const requestAirdrop = useCallback(async () => {
    if (!wallet.publicKey) {
      setAirdropResult({
        status: "fail",
        output: "connect a wallet first",
      });
      return;
    }
    const sol = Number(airdropAmount);
    if (!Number.isFinite(sol) || sol <= 0) {
      setAirdropResult({
        status: "fail",
        output: "invalid amount — enter a positive SOL amount",
      });
      return;
    }
    setAirdropResult({ status: "running", startedAt: Date.now() });
    try {
      const lamports = Math.floor(sol * LAMPORTS_PER_SOL);
      const sig = await connection.requestAirdrop(wallet.publicKey, lamports);
      const latest = await connection.getLatestBlockhash();
      await connection.confirmTransaction(
        {
          signature: sig,
          blockhash: latest.blockhash,
          lastValidBlockHeight: latest.lastValidBlockHeight,
        },
        "confirmed",
      );
      setAirdropResult({
        status: "ok",
        txSig: sig,
        output: `airdropped ${sol} SOL (${lamports} lamports)`,
        finishedAt: Date.now(),
      });
      // Force a balance re-fetch.
      setBalanceTick((n) => n + 1);
    } catch (e) {
      const msg = (e as Error).message ?? String(e);
      setAirdropResult({
        status: "fail",
        output: msg,
        finishedAt: Date.now(),
      });
    }
  }, [connection, wallet.publicKey, airdropAmount]);

  const endpoint = connection.rpcEndpoint;
  const cluster =
    (import.meta.env.VITE_SOLANA_CLUSTER as string | undefined) ?? "(unset)";
  const adapterName = wallet.wallet?.adapter.name ?? "(none)";

  return (
    <StubTabBody summary="Wallet adapter status, current connection, balance, and a localnet/devnet airdrop button.">
      <Info label="endpoint" value={endpoint} />
      <Info label="cluster (env)" value={cluster} />
      <Info label="adapter" value={adapterName} />
      <Info
        label="publicKey"
        value={wallet.publicKey?.toBase58() ?? "(disconnected)"}
      />
      <Info
        label="balance"
        value={
          balance === null
            ? "—"
            : `${(balance / LAMPORTS_PER_SOL).toFixed(6)} SOL (${balance} lamports)`
        }
      />
      <AirdropControl
        amount={airdropAmount}
        onAmountChange={setAirdropAmount}
      />
      <TestRow
        label="requestAirdrop"
        hint="Funds the connected wallet via the RPC's airdrop faucet. Works on surfpool / localnet / devnet. Mainnet will reject."
        result={airdropResult}
        onRun={requestAirdrop}
        disabled={!wallet.publicKey}
      />
      <StubTabRow
        label="sign test message"
        hint="Sign a throwaway message to confirm the connected adapter actually signs. Coming in the next pass."
      />
      <StubTabRow
        label="MWA vs iOS-deeplink path"
        hint="Detect which mobile-wallet path the user is on and confirm the right banner/adapter is active."
      />
    </StubTabBody>
  );
}

function AirdropControl({
  amount,
  onAmountChange,
}: {
  amount: string;
  onAmountChange: (v: string) => void;
}): JSX.Element {
  return (
    <div
      style={{
        padding: "6px 10px",
        background: "#0e0e15",
        borderBottom: "1px solid #1c1c26",
        display: "flex",
        gap: 10,
        alignItems: "center",
      }}
    >
      <div
        style={{
          color: "#7a8098",
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 1,
          minWidth: 130,
        }}
      >
        airdrop amount (SOL)
      </div>
      <input
        type="number"
        min={0}
        step={0.1}
        value={amount}
        onChange={(e) => onAmountChange(e.target.value)}
        style={{
          flex: 1,
          padding: "4px 8px",
          background: "#08080d",
          border: "1px solid #2a2a36",
          borderRadius: 4,
          color: "#e8e8ef",
          fontSize: 11,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div
      style={{
        padding: "6px 10px",
        background: "#0e0e15",
        borderBottom: "1px solid #1c1c26",
        display: "flex",
        gap: 10,
        alignItems: "baseline",
      }}
    >
      <div
        style={{
          color: "#7a8098",
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 1,
          minWidth: 130,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: "#cfd2dc",
          fontSize: 11,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          wordBreak: "break-all",
          flex: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}
